<template>
  <TransitionRoot
    :show="isOpen"
    appear
    as="div"
    class="ui-drawer"
    :style="{ zIndex }"
  >
    <TransitionChild
      v-if="withBack"
      as="template"
      enter="ui-drawer__back-transition"
      leave="ui-drawer__back-transition"
      enter-from="opacity-0"
      enter-to="opacity-100"
      leave-from="opacity-100"
      leave-to="opacity-0"
    >
      <div class="ui-drawer__back" :style="{ zIndex }" @click="handleClose" />
    </TransitionChild>
    <TransitionChild
      :enter-from
      :enter-to
      :leave-from
      :leave-to
      as="template"
      enter="ui-drawer__panel-transition"
      leave="ui-drawer__panel-transition"
      appear
    >
      <div
        ref="drawerRef"
        class="ui-drawer__container"
        :class="containerClasses"
        :style="containerStyles"
      >
        <div
          ref="resizerRef"
          class="ui-drawer__resizer"
          @mousedown="mouseDownResizer"
        />
        <div v-if="isTitleVisible" class="ui-drawer__title" :class="titleClasses">
          <slot name="title"><span class="ui-drawer__title-value">{{ title }}</span></slot>
        </div>
        <div class="ui-drawer__actions" :class="actionsClasses">
          <slot name="actions" />
          <MinimizeButton
            v-if="allowTray"
            data-refid="drawerMinimize"
            tabindex="-1"
            @click="handleMinimize"
          />
          <CloseButton
            data-refid="drawerClose"
            tabindex="-1"
            @click="handleClose"
          />
        </div>
        <DrawerBody :flat>
          <slot />
        </DrawerBody>
      </div>
    </TransitionChild>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import { computed, ref, useSlots } from 'vue'

import { Capacitor } from '@capacitor/core'

import { DrawerDirection, DrawerSize } from './utils/types'

import { LEFT_MINIMUM_MARGIN, MIN_DRAWER_WIDTH } from './utils/const'

import DrawerBody from './DrawerBody.vue'
import MinimizeButton from './components/MinimizeButton.vue'
import CloseButton from './components/CloseButton.vue'
import { TransitionChild, TransitionRoot } from '@headlessui/vue'

type Props = {
  isOpen: boolean
  zIndex: number

  title?: string

  size?: DrawerSize
  direction?: DrawerDirection

  withBack?: boolean

  allowTray?: boolean

  flat?: boolean
}

type Emits = {
  close: []
  minimize: []
}

const { size = 'sm', ...props } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({ name: 'UIDrawer' })

const slots = useSlots()

const drawerRef = ref<HTMLDivElement>()
const resizerRef = ref<HTMLDivElement>()

const drawerWidth = ref(0)

const x = ref(0)
const w = ref(0)

const isNativePlatform = Capacitor.isNativePlatform()

const enterFrom = computed(() => {
  switch (props.direction) {
    case 'to-right':
      return '-translate-x-full'
    case 'to-top':
      return 'translate-y-full'
    case 'to-bottom':
      return '-translate-y-full'
  }
  return 'translate-x-full'
})

const enterTo = computed(() => {
  switch (props.direction) {
    case 'to-right':
      return 'translate-x-0'
    case 'to-top':
    case 'to-bottom':
      return 'translate-y-0'
  }
  return 'translate-x-0'
})

const leaveFrom = computed(() => enterTo.value)
const leaveTo = computed(() => enterFrom.value)


const isTitleVisible = computed(() => !!props.title || slots.title)

const isVerticalDirection = computed(
  () => props.direction && ['to-top', 'to-bottom'].includes(props.direction),
)

const containerClasses = computed(() => {
  const result = [
    `ui-drawer__container--${isVerticalDirection.value ? 'vertical' : 'horizontal'}`,
    `ui-drawer__container--${props.direction || 'to-left'}`,
    `ui-drawer__container--${size}`,
  ]
  if (props.withBack) {
    result.push('ui-drawer__container--with-back')
  }
  return result
})

const isShifted = computed(() => isNativePlatform && !isVerticalDirection.value)

const titleClasses = computed(() => ({
  ['ui-drawer__title--unminimized']: !props.allowTray,
  ['ui-drawer__title--shifted']: isShifted.value,
}))

const actionsClasses = computed(() => ({
  ['ui-drawer__actions--shifted']: isShifted.value,
}))

const containerStyles = computed(() => {
  const result: Record<string, any> = { zIndex: props.zIndex }

  if (drawerWidth.value) {
    if (isVerticalDirection.value) {
      result.height = `${drawerWidth.value}px`
    } else {
      result.width = `${drawerWidth.value}px`
    }
  }

  return result
})

const handleMinimize = () => {
  emit('minimize')
}

const handleClose = () => {
  emit('close')
}

const mouseDownResizer = (e: MouseEvent) => {
  e.preventDefault()
  x.value = isVerticalDirection.value ? e.y : e.x
  const rect = drawerRef.value?.getBoundingClientRect()
  if (rect) {
    w.value = isVerticalDirection.value ? rect.height : rect.width
  }
  window.addEventListener('mousemove', mouseMoveResizer)
  window.addEventListener('mouseup', mouseUpResizer)
}

const mouseUpResizer = () => {
  window.removeEventListener('mousemove', mouseMoveResizer)
  window.removeEventListener('mouseup', mouseUpResizer)

  if (drawerWidth.value < MIN_DRAWER_WIDTH) {
    handleMinimize()
    drawerWidth.value = 0
  }
}

const mouseMoveResizer = (e: MouseEvent) => {
  e.preventDefault()
  const eX = isVerticalDirection.value ? e.y : e.x
  const dx = eX - x.value
  if (w.value - dx >= MIN_DRAWER_WIDTH) {
    const minWidth = isVerticalDirection.value
      ? window.innerHeight
      : window.innerWidth
    drawerWidth.value = Math.min(
      w.value +
        (props.direction && ['to-left', 'to-top'].includes(props.direction)
          ? dx
          : -dx),
      minWidth - LEFT_MINIMUM_MARGIN,
    )
  }
}
</script>

<style scoped>
@import './styles/colors.css';

.ui-drawer {
  &__back-transition {
    @apply transition-opacity ease-linear duration-300;
  }

  &__panel-transition {
    @apply transition-transform ease-in-out duration-500;
  }

  &__back {
    @apply fixed inset-0;
    @apply bg-[rgb(var(--drawer-back)/0.5)];
  }

  &__container {
    @apply min-w-80 md:min-w-0;
    @apply fixed;
    @apply flex flex-col inset-0;
    @apply pointer-events-auto;
    @apply bg-body;
    @apply shadow-dialog;

    &--vertical {
      @apply max-h-full w-screen;
    }

    &--horizontal {
      @apply max-w-full h-screen;
    }

    &--to-top {
      @apply top-auto;
    }

    &--to-bottom {
      @apply bottom-auto;
    }

    &--to-right {
      @apply right-auto;
    }

    &--to-left {
      @apply left-auto;
    }

    &--sm {
      @apply w-1/2 sm:w-[28rem];

      &.ui-drawer__container--vertical {
        @apply w-auto h-1/2;
      }
    }

    &--md {
      @apply w-2/3 sm:w-[36rem];

      &.ui-drawer__container--vertical {
        @apply w-auto h-3/5;
      }
    }

    &--lg {
      @apply w-full sm:w-[48rem];

      &.ui-drawer__container--vertical {
        @apply w-auto h-3/4;
      }
    }

    &--full {
      @apply w-full sm:w-[calc(100vw-8rem)];

      &.ui-drawer__container--vertical {
        @apply w-auto h-[calc(100vh-8rem)];
      }
    }
  }

  :deep(&__content) {
    @apply flex-auto;
    @apply py-4 px-default;
  }

  &__resizer {
    @apply absolute;
    @apply hidden sm:flex shrink-0;
    @apply border-dashed border-gray-200 dark:border-gray-700;
    @apply z-40;
  }

  &__container--horizontal &__resizer {
    @apply w-2;
    @apply inset-y-0;
    @apply cursor-col-resize;
  }

  &__container--vertical &__resizer {
    @apply h-2;
    @apply inset-x-0;
    @apply cursor-row-resize;
  }

  &__container--to-top &__resizer {
    @apply top-0;
    @apply border-t;
  }

  &__container--to-bottom &__resizer {
    @apply bottom-0;
    @apply border-b;
  }

  &__container--to-left &__resizer {
    @apply left-0;
    @apply border-l;
  }

  &__container--to-right &__resizer {
    @apply right-0;
    @apply border-r;
  }

  &__title {
    @apply min-h-12;
    @apply flex items-center;
    @apply shrink-0;
    @apply py-[.555rem] sm:py-1 px-default pr-20;
    @apply border-b border-common-border;
    @apply text-base leading-tight font-medium;
    @apply overflow-hidden;

    &--shifted {
      @apply pt-safe-offset-[.555rem];
    }

    &--unminimized {
      @apply pr-12;
    }
  }

  &__title-value {
    @apply truncate;
  }

  &__actions {
    @apply absolute top-1 sm:top-2 right-1 sm:right-2;
    @apply flex items-center gap-1;

    &--shifted {
      @apply top-safe-offset-1;
    }
  }
}
</style>
